import React from "react";

const SPIDOMETER_SCORES = [
  { max: 500, color: "#DF6897", text: "Žemiausias" },
  { max: 560, color: "#845EA1", text: "Žemas" },
  { max: 620, color: "#51569F", text: "Vidutinis" },
  { max: 680, color: "#4973B4", text: "Aukštas" },
  { max: 801, color: "#66C3E5", text: "Aukščiausias" },
];

const NO_SPIDOMETER_SCORE = { color: null, max: -1, text: "" };

export const Spidometer = ({
  width = "100%",
  height,
  score,
  riskProbability = "-",
  noUpdateTime,
  minimalSpidometerInfo,
}) => {
  const scoreRotateAngle = score ? ((score - 200) * 180) / 600 : 0;

  const currentScore = score
    ? SPIDOMETER_SCORES.find((itm) => itm.max > score) ?? NO_SPIDOMETER_SCORE
    : NO_SPIDOMETER_SCORE;

  const calculateAngle = () => {
    const O = { x: 0, y: 63 }; // inner circle center
    const X = { x: -160, y: 0 }; // scoreBar left side min point
    const pi = Math.PI;

    const A = {
      x: Math.cos(((180 - scoreRotateAngle) * pi) / 180) * 160,
      y: Math.sin(((180 - scoreRotateAngle) * pi) / 180) * 160,
    }; // Point on outer circle

    const XO = Math.sqrt(Math.pow(O.x - X.x, 2) + Math.pow(O.y - X.y, 2));
    const OA = Math.sqrt(Math.pow(O.x - A.x, 2) + Math.pow(O.y - A.y, 2));
    const XA = Math.sqrt(Math.pow(A.x - X.x, 2) + Math.pow(A.y - X.y, 2));
    const radians = Math.acos((OA * OA + XO * XO - XA * XA) / (2 * OA * XO));

    let angle = radians * (180 / pi);

    const SCORE_WHEN_DEGREES_BECOME_MORE_THEN_180 = 655; // bruteforced value when angle becomes more then 180 degrees
    if (score && score > SCORE_WHEN_DEGREES_BECOME_MORE_THEN_180) {
      angle = 360 - angle;
    }
    return angle;
  };

  return (
    <svg width={width ?? 360} height={height ?? 225} viewBox="0 0 360 225" xmlns="http://www.w3.org/2000/svg">
      <g>
        <defs>
          <linearGradient y2="1.2005" x2="1.09213" y1="1.12904" x1="0.12534" id="spidometerGradient">
            <stop stopColor="#2C2567" offset="0.00833" />
            <stop stopColor="#9E67AB" offset="0.10254" />
            <stop stopColor="#F37294" offset="0.17551" />
            <stop stopColor="#FBA68E" offset="0.2981" />
            <stop stopColor="#F2739C" offset="0.40921" />
            <stop stopColor="#4566B0" offset="0.66979" />
            <stop stopColor="#3D83C2" offset="0.82604" />
            <stop stopColor="#3EB8E1" offset="1" />
          </linearGradient>
          <radialGradient
            id="scoreGradient"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 13.0026) rotate(14.6726) scale(25.667 873.34)"
          >
            <stop stopColor="#FAC58D" />
            <stop offset="0.125" stopColor="#EE6591" />
            <stop offset="0.328125" stopColor="#AB5089" />
            <stop offset="0.614583" stopColor="#7542CD" />
            <stop offset="0.765625" stopColor="#693EDE" />
            <stop offset="0.875" stopColor="#1F9DF3" />
            <stop offset="1" stopColor="#11C5FA" />
          </radialGradient>
          <filter
            id="centerCircleGradient"
            x="161"
            y="164"
            width="108"
            height="108"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_77_974" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_77_974" result="shape" />
          </filter>
        </defs>
        {!minimalSpidometerInfo && (
          <g>
            <path
              stroke="null"
              d="m7.0876,219.62512c1.01243,-0.85139 1.80863,-1.54982 2.3887,-2.09336c0.59055,-0.55412 1.08094,-1.12845 1.47118,-1.72394c0.39024,-0.59549 0.58531,-1.19002 0.58531,-1.78551c0,-0.61569 -0.15295,-1.09767 -0.45876,-1.44688c-0.29532,-0.34921 -0.76456,-0.52334 -1.40791,-0.52334c-0.62219,0 -1.10734,0.19529 -1.45536,0.58491c-0.33744,0.38 -0.51679,0.89276 -0.53785,1.53923l-1.74011,0c0.03164,-1.16982 0.39024,-2.06257 1.0757,-2.67827c0.69604,-0.62628 1.57668,-0.93893 2.6418,-0.93893c1.14956,0 2.04591,0.30785 2.68926,0.92354c0.65383,0.61569 0.98079,1.4363 0.98079,2.46277c0,0.73883 -0.19507,1.45169 -0.58531,2.13954c-0.37966,0.67726 -0.83842,1.28815 -1.37627,1.83169c-0.52727,0.53392 -1.20226,1.15443 -2.02486,1.86247l-0.71186,0.61569l5.01468,0l0,1.46227l-7.68812,0l0,-1.27756l1.13898,-0.95433l0.00001,0.00001zm8.13343,-3.37092c0,-1.77493 0.3058,-3.16024 0.91751,-4.15593c0.62219,-1.00531 1.68741,-1.50845 3.19547,-1.50845c1.50806,0 2.56795,0.50314 3.17966,1.50845c0.62219,0.99569 0.93333,2.381 0.93333,4.15593c0,1.79609 -0.31114,3.20161 -0.93333,4.2175c-0.61171,1.00531 -1.67159,1.50845 -3.17966,1.50845c-1.50806,0 -2.57329,-0.50314 -3.19547,-1.50845c-0.61171,-1.01589 -0.91751,-2.42141 -0.91751,-4.2175zm6.45423,0c0,-0.83119 -0.05804,-1.53442 -0.17401,-2.10875c-0.10549,-0.57433 -0.32696,-1.04187 -0.66441,-1.4007c-0.33744,-0.36942 -0.83842,-0.55412 -1.50282,-0.55412c-0.66441,0 -1.16538,0.18471 -1.50282,0.55412c-0.33744,0.35883 -0.56425,0.82638 -0.68022,1.4007c-0.10549,0.57433 -0.15819,1.27756 -0.15819,2.10875c0,0.86197 0.0527,1.58541 0.15819,2.17032c0.10549,0.58491 0.32696,1.05726 0.66441,1.4161c0.34802,0.35883 0.85424,0.53873 1.51864,0.53873c0.66441,0 1.16538,-0.1799 1.50282,-0.53873c0.34802,-0.35883 0.57473,-0.83119 0.68022,-1.4161c0.10549,-0.58491 0.15819,-1.30835 0.15819,-2.17032zm3.67994,0c0,-1.77493 0.3058,-3.16024 0.91751,-4.15593c0.62219,-1.00531 1.68741,-1.50845 3.19547,-1.50845c1.50806,0 2.56795,0.50314 3.17966,1.50845c0.62219,0.99569 0.93333,2.381 0.93333,4.15593c0,1.79609 -0.31114,3.20161 -0.93333,4.2175c-0.61171,1.00531 -1.67159,1.50845 -3.17966,1.50845c-1.50806,0 -2.57329,-0.50314 -3.19547,-1.50845c-0.61171,-1.01589 -0.91751,-2.42141 -0.91751,-4.2175zm6.45423,0c0,-0.83119 -0.05804,-1.53442 -0.17401,-2.10875c-0.10549,-0.57433 -0.32696,-1.04187 -0.66441,-1.4007c-0.33744,-0.36942 -0.83842,-0.55412 -1.50282,-0.55412c-0.66441,0 -1.16538,0.18471 -1.50282,0.55412c-0.33744,0.35883 -0.56425,0.82638 -0.68022,1.4007c-0.10549,0.57433 -0.15819,1.27756 -0.15819,2.10875c0,0.86197 0.0527,1.58541 0.15819,2.17032c0.10549,0.58491 0.32696,1.05726 0.66441,1.4161c0.34802,0.35883 0.85424,0.53873 1.51864,0.53873c0.66441,0 1.16538,-0.1799 1.50282,-0.53873c0.34802,-0.35883 0.57473,-0.83119 0.68022,-1.4161c0.10549,-0.58491 0.15819,-1.30835 0.15819,-2.17032z"
              fill="#2C2567"
            />
            <path
              stroke="null"
              fill="#3DAAD8"
              d="m329.37893,216.14646c-1.09647,-0.53392 -1.64519,-1.37473 -1.64519,-2.52434c0,-0.55412 0.14237,-1.06207 0.42712,-1.52384c0.28475,-0.46177 0.71186,-0.82638 1.28135,-1.09286c0.56949,-0.27706 1.26553,-0.41559 2.08813,-0.41559c0.81271,0 1.50282,0.13853 2.07231,0.41559c0.58037,0.26648 1.01243,0.63109 1.29717,1.09286c0.28475,0.46177 0.42712,0.96972 0.42712,1.52384c0,0.57433 -0.15226,1.08227 -0.45876,1.52384c-0.29463,0.43099 -0.69011,0.76481 -1.18644,1.0005c0.60113,0.21549 1.0757,0.56952 1.42373,1.06207c0.34802,0.48197 0.52203,1.05149 0.52203,1.70855c0,0.66668 -0.17401,1.25736 -0.52203,1.77012c-0.34802,0.51276 -0.83248,0.90815 -1.45536,1.18521c-0.62189,0.27706 -1.32881,0.41559 -2.11977,0.41559c-0.79096,0 -1.49689,-0.13853 -2.11977,-0.41559c-0.61102,-0.27706 -1.09152,-0.67245 -1.43955,-1.18521c-0.34802,-0.51276 -0.52203,-1.10344 -0.52203,-1.77012c0,-0.66668 0.17401,-1.24197 0.52203,-1.72394c0.34802,-0.48197 0.81765,-0.83119 1.40791,-1.04668zm4.22372,-2.30885c0,-0.57433 -0.1839,-1.01589 -0.55367,-1.32374c-0.36878,-0.30785 -0.875,-0.46177 -1.51864,-0.46177c-0.63277,0 -1.13305,0.15392 -1.50282,0.46177c-0.36878,0.30785 -0.55367,0.75422 -0.55367,1.33913c0,0.51276 0.18983,0.93412 0.56949,1.26217c0.39054,0.31843 0.88587,0.47716 1.487,0.47716c0.60113,0 1.09746,-0.16451 1.487,-0.49255c0.39054,-0.32805 0.58531,-0.74941 0.58531,-1.26217zm-2.07231,3.07847c-0.69604,0 -1.26553,0.16932 -1.70847,0.50795c-0.43206,0.32805 -0.64859,0.80521 -0.64859,1.43149c0,0.58491 0.21158,1.06207 0.63277,1.43149c0.42217,0.35883 0.99661,0.53873 1.72429,0.53873c0.7178,0 1.28135,-0.18471 1.69265,-0.55412c0.42217,-0.36942 0.63277,-0.84177 0.63277,-1.4161c0,-0.61569 -0.21554,-1.09286 -0.64859,-1.43149c-0.42119,-0.33863 -0.98079,-0.50795 -1.67683,-0.50795zm5.99152,-0.66187c0,-1.77493 0.30551,-3.16024 0.91751,-4.15593c0.62189,-1.00531 1.68672,-1.50845 3.19547,-1.50845c1.50777,0 2.56765,0.50314 3.17966,1.50845c0.62189,0.99569 0.93333,2.381 0.93333,4.15593c0,1.79609 -0.31144,3.20161 -0.93333,4.2175c-0.612,1.00531 -1.67189,1.50845 -3.17966,1.50845c-1.50875,0 -2.57358,-0.50314 -3.19547,-1.50845c-0.612,-1.01589 -0.91751,-2.42141 -0.91751,-4.2175zm6.45423,0c0,-0.83119 -0.05833,-1.53442 -0.17401,-2.10875c-0.10579,-0.57433 -0.32726,-1.04187 -0.66441,-1.4007c-0.33814,-0.36942 -0.83842,-0.55412 -1.50282,-0.55412c-0.66441,0 -1.16568,0.18471 -1.50282,0.55412c-0.33814,0.35883 -0.56455,0.82638 -0.68022,1.4007c-0.10579,0.57433 -0.15819,1.27756 -0.15819,2.10875c0,0.86197 0.0524,1.58541 0.15819,2.17032c0.1048,0.58491 0.32627,1.05726 0.66441,1.4161c0.34802,0.35883 0.85424,0.53873 1.51864,0.53873c0.66441,0 1.16469,-0.1799 1.50282,-0.53873c0.34802,-0.35883 0.57443,-0.83119 0.68022,-1.4161c0.1048,-0.58491 0.15819,-1.30835 0.15819,-2.17032zm3.67994,0c0,-1.77493 0.30551,-3.16024 0.91751,-4.15593c0.62189,-1.00531 1.68672,-1.50845 3.19547,-1.50845c1.50777,0 2.56765,0.50314 3.17966,1.50845c0.62189,0.99569 0.93333,2.381 0.93333,4.15593c0,1.79609 -0.31144,3.20161 -0.93333,4.2175c-0.612,1.00531 -1.67189,1.50845 -3.17966,1.50845c-1.50875,0 -2.57358,-0.50314 -3.19547,-1.50845c-0.612,-1.01589 -0.91751,-2.42141 -0.91751,-4.2175zm6.45423,0c0,-0.83119 -0.05833,-1.53442 -0.17401,-2.10875c-0.10579,-0.57433 -0.32726,-1.04187 -0.66441,-1.4007c-0.33814,-0.36942 -0.83842,-0.55412 -1.50282,-0.55412c-0.66441,0 -1.16568,0.18471 -1.50282,0.55412c-0.33814,0.35883 -0.56455,0.82638 -0.68022,1.4007c-0.10579,0.57433 -0.15819,1.27756 -0.15819,2.10875c0,0.86197 0.0524,1.58541 0.15819,2.17032c0.1048,0.58491 0.32627,1.05726 0.66441,1.4161c0.34802,0.35883 0.85424,0.53873 1.51864,0.53873c0.66441,0 1.16469,-0.1799 1.50282,-0.53873c0.34802,-0.35883 0.57443,-0.83119 0.68022,-1.4161c0.1048,-0.58491 0.15819,-1.30835 0.15819,-2.17032z"
            />
          </g>
        )}
        <g>
          <path
            strokeLinecap="round"
            strokeWidth="20"
            d="m340.31641,187.813c-4.98651,-223.57066 -314.41709,-223.35299 -320.31653,-3"
            fill="#000000"
            fillOpacity="0"
            stroke="url(#spidometerGradient)"
          />
          <rect height="18" width="2.2" y="10.97289" x="179.10935" fill="#ffffff" />
          <rect
            transform="rotate(36 274.6307067871093,49.851039886474595) "
            height="18"
            width="2.2"
            y="40.85104"
            x="273.53066"
            fill="#ffffff"
          />
          <rect
            transform="rotate(18 229.62063598632818,27.51473617553714) "
            height="18"
            width="2.2"
            y="18.51474"
            x="228.52062"
            fill="#ffffff"
          />
          <rect
            transform="rotate(54 309.7136840820312,85.85332489013675) "
            height="18"
            width="2.2"
            y="76.85333"
            x="308.61369"
            fill="#ffffff"
          />
        </g>
        {currentScore.max !== -1 ? (
          <>
            {!minimalSpidometerInfo && (
              <g transform={`rotate(${scoreRotateAngle ?? 0} 180 180)`}>
                <rect
                  fill="#000000"
                  strokeWidth="0"
                  strokeDasharray="null"
                  strokeLinecap="round"
                  fillOpacity="0"
                  width="360"
                  height="360"
                  stroke="black"
                />
                <g transform="translate(7.3 166.8)">
                  <circle cx="12.6943" cy="13.0026" r="13" fill="url(#scoreGradient)" />
                </g>

                <circle fill="white" r="10" cy="180" cx="20" />
                <circle fill="#845EA1" r="5" cy="180" cx="20" />
              </g>
            )}

            <g transform={`rotate(${calculateAngle()} 180 114)`}>
              <rect
                stroke="null"
                fill="null"
                opacity="0"
                x="110.29574"
                y="43.5221"
                width="140.48397"
                height="141.4546"
                transform="rotate(0.2794893980026245 180.5377197265587,114.24940490722847) "
              />
              <circle id="white" stroke="#E1E1E1" fill="white" r="43.5" cy="113" cx="180" />
              <circle fill="white" r="50" cy="113.3789" cx="180.27083" stroke="null" />
              <polygon
                transform="rotate(157.5 123.56388854980469,136.6556396484375) "
                strokeLinejoin="round"
                fill="#000000"
                points="130.19263863563538,136.65566182136536 116.93513131141663,145.16413044929504 116.93513131141663,128.14717030525208 130.19263863563538,136.65566182136536 "
                strokeWidth="3"
                stroke="#000000"
                filter="drop-shadow(1px 1.5px 2px rgb(0 0 0 / 0.5))"
              />
            </g>
          </>
        ) : null}
        <g transform="translate(-50, -119) scale(1.07)">
          <g filter="url(#centerCircleGradient)">
            <circle cx="215" cy="217" r="50" fill="white" />
          </g>
        </g>
        <circle stroke="#E1E1E1" fill="white" r="43.5" cy="113" cx="180" />
        <text
          fontWeight="700"
          textAnchor="middle"
          fontFamily="Roboto, serif"
          fontSize="24"
          y={score ? "115" : "122"}
          x="180"
          strokeWidth="0"
          stroke="#000000"
          fill="#000000"
        >
          {score || "-"}
        </text>
        <text
          fontWeight="bold"
          textAnchor="middle"
          fontFamily="Roboto, serif"
          fontSize="10"
          y="130"
          x="180"
          strokeWidth="0"
          stroke="#000000"
          fill={currentScore?.color ?? "black"}
        >
          {currentScore?.text ?? "-"}
        </text>

        {!noUpdateTime && (
          <text
            fontWeight="400"
            textAnchor="middle"
            fontFamily="Roboto, serif"
            fontSize="12"
            y="195"
            x="180"
            strokeWidth="0"
            stroke="#000000"
            fill="#999999"
          >
            {`Bankroto tikimybė per ateinančius 12 mėn. ${riskProbability || "-"}%`}
          </text>
        )}
      </g>
    </svg>
  );
};
